/**
 * Step 07 — Employment & Occupation
 *
 * Fields: employmentStatus, occupation, workShift, comfortableWithShift,
 * desiredShift, incomeRange, dreamOccupation
 *
 * @module udp-schema/step-07
 */

import { z } from 'zod';
import { OCCUPATION_VALUES } from './shared-enums.js';

// ─── Main Step 07 Schema ────────────────────────────────────────────────────

export const step07EmploymentSchema = z.object({
  /** Employment status */
  employmentStatus: z
    .enum([
      'employed',
      'self_employed',
      'unemployed',
      'student',
      'homemaker',
      'retired',
      'daily_wage',
      'other',
    ])
    .optional(),

  /** Occupation — canonical 39-value taxonomy from `shared-enums`.
   *  Shared with dating-schema's `profession` and any future service
   *  profile's occupation field. */
  occupation: z.enum(OCCUPATION_VALUES).optional(),

  /** Current work shift */
  workShift: z
    .enum(['day_shift', 'night_shift', 'rotating', 'flexible', 'work_from_home', 'not_applicable'])
    .optional(),

  /** Comfortable with current shift? */
  comfortableWithShift: z.enum(['yes', 'no', 'somewhat']).optional(),

  /** Desired shift */
  desiredShift: z
    .enum(['day_shift', 'night_shift', 'rotating', 'flexible', 'work_from_home', 'not_applicable'])
    .optional(),

  /** Income range */
  incomeRange: z.string().trim().optional(),

  /** Dream occupation — same canonical 39-value enum as `occupation`,
   *  so the two fields are directly comparable. */
  dreamOccupation: z.enum(OCCUPATION_VALUES).optional(),
});

// ─── Inferred types ─────────────────────────────────────────────────────────

export type Step07EmploymentInput = z.infer<typeof step07EmploymentSchema>;
